import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Grid, CircularProgress, Alert, Chip, Button,
  IconButton, Divider, List, ListItem,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { apiService } from '../utils/api';

const statusColor = (s) =>
  s === 'confirmed' ? 'success' : s === 'in_progress' ? 'info' : s === 'cancelled' ? 'error' : 'warning';

const pad2 = (n) => String(n).padStart(2, '0');
const toMonthStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const toDateStr = (y, m, day) => `${y}-${pad2(m + 1)}-${pad2(day)}`;

export default function PosterReservations() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shifts, setShifts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  const month = toMonthStr(current);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get('/api/users/my-reservation-management', { params: { month } });
      const data = response?.data || response || {};
      setShifts(data.shifts || []);
      setReservations(data.reservations || []);
    } catch (err) {
      setError(err?.response?.data?.error || '予約管理データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const y = current.getFullYear();
  const m = current.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();

  const shiftsByDate = {};
  shifts.forEach((s) => { (shiftsByDate[s.date] = shiftsByDate[s.date] || []).push(s); });
  const resByDate = {};
  reservations.forEach((r) => { (resByDate[r.reservation_date] = resByDate[r.reservation_date] || []).push(r); });

  // 前月より前には戻れない（データ保持が前月まで）
  const today = new Date();
  const minMonthValue = today.getFullYear() * 12 + (today.getMonth() - 1);
  const canGoPrev = (y * 12 + m) > minMonthValue;
  const prevMonth = () => { if (canGoPrev) setCurrent(new Date(y, m - 1, 1)); };
  const nextMonth = () => setCurrent(new Date(y, m + 1, 1));

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedShifts = selectedDate ? (shiftsByDate[selectedDate] || []) : [];
  const selectedRes = selectedDate ? (resByDate[selectedDate] || []) : [];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>予約管理</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        あなたのシフトと、あなた宛の予約状況を確認できます。日付をクリックすると詳細が表示されます。
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={prevMonth} disabled={!canGoPrev}><ChevronLeftIcon /></IconButton>
          <Typography variant="h6">{y}年 {m + 1}月</Typography>
          <IconButton onClick={nextMonth}><ChevronRightIcon /></IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Grid container columns={7} spacing={0.5}>
            {['日', '月', '火', '水', '木', '金', '土'].map((w) => (
              <Grid item xs={1} key={w} sx={{ textAlign: 'center', fontWeight: 700, py: 0.5 }}>{w}</Grid>
            ))}
            {cells.map((d, idx) => {
              if (d === null) return <Grid item xs={1} key={`e${idx}`} />;
              const dateStr = toDateStr(y, m, d);
              const sCount = (shiftsByDate[dateStr] || []).length;
              const rCount = (resByDate[dateStr] || []).length;
              const isSelected = selectedDate === dateStr;
              return (
                <Grid item xs={1} key={dateStr}>
                  <Box
                    onClick={() => setSelectedDate(dateStr)}
                    sx={{
                      border: '1px solid #e0e0e0', borderRadius: 1, minHeight: 64, p: 0.5, cursor: 'pointer',
                      backgroundColor: isSelected ? 'primary.light' : (sCount > 0 ? '#f1f8e9' : 'transparent'),
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{d}</Typography>
                    {sCount > 0 && <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>出勤{sCount}</Typography>}
                    {rCount > 0 && <Typography variant="caption" sx={{ display: 'block', color: 'primary.main' }}>予約{rCount}</Typography>}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      {selectedDate && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>{selectedDate} の詳細</Typography>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>出勤シフト</Typography>
          {selectedShifts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">この日のシフトはありません</Typography>
          ) : (
            <List dense>
              {selectedShifts.map((s) => (
                <ListItem key={s.id}>
                  <Typography variant="body2">{s.start_time} 〜 {s.end_time}{s.is_next_day ? '（翌日）' : ''}</Typography>
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>予約</Typography>
          {selectedRes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">この日の予約はありません</Typography>
          ) : (
            <List dense>
              {selectedRes.map((r) => (
                <ListItem key={r.id} sx={{ flexDirection: 'column', alignItems: 'stretch', border: '1px solid #eee', borderRadius: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip label={r.status_label || '未対応'} size="small" color={statusColor(r.status)} />
                    {r.cancel_requested && <Chip label="キャンセル依頼あり" size="small" color="warning" variant="outlined" />}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.start_time} 〜 {r.end_time}（{r.course_minutes}分）
                    </Typography>
                  </Box>
                  {r.hotel && <Typography variant="body2">ホテル（場所）: {r.hotel}</Typography>}
                  {r.memo && <Typography variant="body2" color="text.secondary">その他要望: {r.memo}</Typography>}
                  <Button size="small" variant="outlined" sx={{ mt: 1, alignSelf: 'flex-start' }} onClick={() => navigate(`/reservations/${r.id}`)}>
                    詳細を見る
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Container>
  );
}
